import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, PartyPopper } from "lucide-react";
import { paymentStatus } from "@/lib/api";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ payment_status: "pending" });
    const [attempts, setAttempts] = useState(0);
    const [expired, setExpired] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;

        async function poll() {
            try {
                const s = await paymentStatus(sessionId);
                if (cancelled) return;
                setStatus(s);
                if (
                    s.payment_status === "paid" ||
                    s.payment_status === "failed" ||
                    s.payment_status === "expired"
                ) {
                    return;
                }
            } catch {
                // ignore, will retry
            }
            setAttempts((a) => {
                const next = a + 1;
                if (next >= 15) {
                    setExpired(true);
                    return next;
                }
                timerRef.current = setTimeout(poll, 2000);
                return next;
            });
        }
        poll();
        return () => {
            cancelled = true;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [sessionId]);

    const paid = status.payment_status === "paid";

    return (
        <div
            data-testid="payment-success-page"
            className="mx-auto max-w-2xl px-4 py-24 sm:px-6 lg:px-8"
        >
            <div className="card-surface p-10 text-center">
                {paid ? (
                    <>
                        <PartyPopper className="mx-auto h-10 w-10 text-[color:var(--brand-green)]" />
                        <h1 className="mt-4 font-display text-3xl font-extrabold text-[color:var(--brand-navy)]">
                            Booking confirmed!
                        </h1>
                        <p className="mt-3 text-slate-600">
                            Your scooter is reserved. We&apos;ll email you the
                            details and coordinate delivery. Freedom Delivered.
                        </p>
                        {status.amount_total && (
                            <div
                                data-testid="payment-amount"
                                className="mt-6 inline-flex items-center gap-2 rounded-full bg-green-50 px-4 py-2 text-sm font-semibold text-green-700"
                            >
                                <CheckCircle2 className="h-4 w-4" />
                                Paid ${Number(status.amount_total).toFixed(2)}
                            </div>
                        )}
                        <div className="mt-8 flex justify-center gap-3">
                            <Link
                                to="/"
                                data-testid="success-home-link"
                                className="pill-btn border border-slate-200 bg-white text-[color:var(--brand-navy)]"
                            >
                                Return home
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="success-contact-link"
                                className="hero-cta pill-btn text-white"
                            >
                                Contact us
                            </Link>
                        </div>
                    </>
                ) : expired ? (
                    <>
                        <h1 className="font-display text-2xl font-extrabold text-[color:var(--brand-navy)]">
                            Payment is taking longer than usual.
                        </h1>
                        <p className="mt-3 text-slate-600">
                            No worries — check your email for a Stripe receipt.
                            If you don&apos;t see confirmation within an hour,
                            contact us with your session ID.
                        </p>
                        <p
                            data-testid="payment-session-id"
                            className="mt-4 break-all text-xs text-slate-500"
                        >
                            Session: {sessionId}
                        </p>
                    </>
                ) : (
                    <>
                        <Loader2 className="mx-auto h-10 w-10 animate-spin text-[color:var(--brand-navy)]" />
                        <h1 className="mt-4 font-display text-2xl font-extrabold text-[color:var(--brand-navy)]">
                            Confirming your payment…
                        </h1>
                        <p className="mt-3 text-slate-600">
                            This usually takes just a few seconds. Please
                            don&apos;t close this page.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
